'use client';

import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { generateRoomId } from '@/lib/client-utils';
import {
  roleColor,
  roleLabel,
  SignInPanel,
  useMeetSession,
  type Session,
} from '@/lib/MeetAuth';
import styles from '../styles/Landing.module.css';

/**
 * CA Kedia Meet landing page.
 *
 * Sign-in used to be an email/password form owned by this file, holding the
 * result in React state — so the identity died on navigation to `/rooms/x` and
 * every joiner was an anonymous guest as far as the server was concerned. All
 * four sign-in paths now live in `lib/MeetAuth.tsx` and converge on the HttpOnly
 * session cookie, which is what actually travels into the room.
 */
export default function Page() {
  const router = useRouter();
  const { session, setSession, loading, refresh, logout } = useMeetSession();
  const [ssoBusy, setSsoBusy] = useState(false);
  const [code, setCode] = useState('');
  const [creating, setCreating] = useState(false);

  // ── App hand-off (?sso=…) ─────────────────────────────────────────────────
  // /api/sso now also sets the session cookie, so this only has to fire once and
  // the identity persists from then on.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sso = params.get('sso');
    if (!sso) return;
    setSsoBusy(true);
    fetch('/api/sso', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token: sso }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d: Session) => setSession(d))
      .catch(() => {})
      .finally(() => {
        // Strip the token from the URL either way — it should not sit in history.
        window.history.replaceState({}, '', '/');
        setSsoBusy(false);
        refresh();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newMeeting = async () => {
    const room = generateRoomId();
    setCreating(true);
    try {
      // Register the meeting before navigating, so the room has a lobby for
      // guests to wait in from the very first join. Best-effort: a failure still
      // opens the room, it just behaves like a legacy unrecorded one.
      await fetch('/api/meetings/create', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ room, title: 'CA Kedia Meeting' }),
      }).catch(() => {});
    } finally {
      setCreating(false);
      router.push(`/rooms/${room}`);
    }
  };

  const joinCode = () => {
    const c = code.trim();
    if (c) router.push(`/rooms/${encodeURIComponent(c)}`);
  };
  const canCreate = !!session && (session.role === 'admin' || session.role === 'team');

  return (
    <div className={styles.page}>
      <div className={styles.bg} aria-hidden="true">
        <span className={`${styles.blob} ${styles.blob1}`} />
        <span className={`${styles.blob} ${styles.blob2}`} />
        <span className={`${styles.blob} ${styles.blob3}`} />
        <img src="/cakedia-logo.png" alt="" className={styles.watermark} />
      </div>
      <header className={styles.nav}>
        <div className={styles.brand}>
          <img src="/cakedia-logo.png" alt="" width="34" height="34" />
          <span>CA Kedia Meet</span>
        </div>
        {session && (
          <div className={styles.user}>
            <span
              className={styles.badge}
              style={{ background: roleColor[session.role] + '22', color: roleColor[session.role] }}
            >
              {roleLabel[session.role]}
            </span>
            <span className={styles.uname}>{session.name}</span>
            <button className={styles.link} onClick={logout}>
              Sign out
            </button>
          </div>
        )}
      </header>

      <main className={styles.main}>
        {loading || ssoBusy ? (
          <div className={styles.spinner} />
        ) : !session ? (
          <SignInPanel styles={styles} onSignedIn={(s) => setSession(s)} />
        ) : (
          <div className={styles.hero}>
            <div className={styles.heroCopy}>
              <h1>
                Secure video meetings
                <br />
                for Aayush Kedia &amp; Company
              </h1>
              <p className={`${styles.muted} ${styles.big}`}>
                Chartered Accountants, Surat — connect with your CA team over encrypted video.
              </p>
              <div className={styles.actions}>
                {canCreate ? (
                  <>
                    <button
                      className={`${styles.primary} ${styles.primaryLg}`}
                      onClick={newMeeting}
                      disabled={creating}
                    >
                      <span className={styles.ic}>＋</span>{' '}
                      {creating ? 'Starting…' : 'New meeting'}
                    </button>
                    <div className={styles.joinbox}>
                      <input
                        className={styles.input}
                        placeholder="Enter a code or link"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') joinCode();
                        }}
                      />
                      <button
                        className={`${styles.link} ${styles.blue}`}
                        onClick={joinCode}
                        disabled={!code.trim()}
                      >
                        Join
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.joinbox}>
                      <input
                        className={styles.input}
                        placeholder="Enter meeting code or link"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') joinCode();
                        }}
                      />
                      <button className={styles.primary} onClick={joinCode} disabled={!code.trim()}>
                        Join
                      </button>
                    </div>
                    <p className={`${styles.tiny} ${styles.muted}`}>
                      Meetings are created by the CA Kedia team. Join with a code or an invite link.
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className={styles.heroArt}>
              <div className={styles.artCard}>
                <img
                  src="/cakedia-logo.png"
                  alt=""
                  width="120"
                  height="120"
                  style={{ borderRadius: 24 }}
                />
                <div className={styles.artDots}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className={styles.foot}>
        <span>© Aayush Kedia &amp; Company, Chartered Accountants — Surat</span>
        <nav>
          <a
            href="https://play.google.com/store/apps/details?id=com.cakedia.app"
            target="_blank"
            rel="noopener"
          >
            Get the app
          </a>
          <a href="https://aayush-kedia-and-company.web.app" target="_blank" rel="noopener">
            Web portal
          </a>
          <a href="https://cakedia.com" target="_blank" rel="noopener">
            cakedia.com
          </a>
        </nav>
      </footer>
    </div>
  );
}
